<template>
  <v-container fluid>
    <v-card>
      <v-card-title class="py-2">
        <span>ดูสต๊อกสินค้า / รายงานซื้อตามผู้จำหน่าย</span>
      </v-card-title>
      <v-card-text class="pt-2">
        <v-radio-group v-model="stock_mode" row class="mb-4" hide-details>
          <v-radio label="คลัง/สาขา (สต๊อกสินค้า)" value="stock" color="#86bc3a" />
          <v-radio label="Supplier (รายงานซื้อตามผู้จำหน่าย)" value="supplier" color="#86bc3a" />
        </v-radio-group>

        <!-- โหมดคลัง/สาขา -->
        <template v-if="stock_mode === 'stock'">
        <v-row class="mb-2">
          <v-col cols="12">
            <v-autocomplete
              v-model="selected_products_stock"
              :items="products"
              item-text="name"
              item-value="id"
              label="เลือกสินค้า"
              color="#86bc3a"
              multiple
              chips
              deletable-chips
              prepend-inner-icon="mdi-package-variant"
              outlined
              dense
              small-chips
              hide-details="auto"
            />
          </v-col>
        </v-row>
        <v-row class="mb-2">
          <v-col cols="12" md="4">
            <v-select
              v-model="selected_branch_stock"
              :items="branches_stock"
              item-text="name"
              item-value="id"
              label="เลือกสาขา"
              color="#86bc3a"
              outlined
              dense
              hide-details="auto"
              clearable
              prepend-inner-icon="mdi-store"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-menu
              v-model="start_date_menu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="start_date_stock"
                  label="วันที่เริ่มต้น *"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  color="#86bc3a"
                  outlined
                  dense
                  hide-details="auto"
                  :rules="[v => !!v || 'กรุณาเลือกวันที่เริ่มต้น']"
                />
              </template>
              <v-date-picker
                v-model="start_date_stock"
                @input="start_date_menu = false"
                color="#86bc3a"
              />
            </v-menu>
          </v-col>
          <v-col cols="12" md="4">
            <v-menu
              v-model="end_date_menu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="end_date_stock"
                  label="วันที่สิ้นสุด *"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  color="#86bc3a"
                  outlined
                  dense
                  hide-details="auto"
                  :rules="[v => !!v || 'กรุณาเลือกวันที่สิ้นสุด']"
                  :min="start_date_stock"
                />
              </template>
              <v-date-picker
                v-model="end_date_stock"
                @input="end_date_menu = false"
                color="#86bc3a"
                :min="start_date_stock"
              />
            </v-menu>
          </v-col>
        </v-row>

        <v-row v-if="loading_stock_data" class="mb-4">
          <v-col cols="12" class="text-center">
            <v-progress-circular indeterminate color="#86bc3a" />
            <div class="mt-2">กำลังโหลดข้อมูล...</div>
          </v-col>
        </v-row>

        <div v-else-if="stock_data_results.length > 0" class="mt-4">
          <div v-for="(result, index) in stock_data_results" :key="index" class="mb-6">
            <v-card elevation="3" class="mb-4">
              <v-card-title class="grad-success-bg white--text py-3">
                <v-icon left color="white">mdi-package-variant</v-icon>
                {{ getProductName(result.product_id) }}
              </v-card-title>
              <v-card-text class="pa-4">
                <div v-if="result.data && result.data.length > 0">
                  <div v-for="(warehouse, wIndex) in result.data" :key="wIndex" class="mb-4">
                    <v-card outlined class="mb-3" style="border-left: 4px solid #86bc3a;">
                      <v-card-subtitle class="font-weight-bold pa-3" style="background-color: #f5f5f5;">
                        <div class="d-flex align-center">
                          <v-icon :color="warehouse.is_main ? 'green' : 'blue'" class="mr-2" size="24">
                            {{ warehouse.is_main ? 'mdi-warehouse' : 'mdi-store' }}
                          </v-icon>
                          <span class="text-h6">{{ warehouse.warehouse }}</span>
                          <v-chip v-if="warehouse.is_main" small color="green" text-color="white" class="ml-3">
                            คลังหลัก
                          </v-chip>
                        </div>
                      </v-card-subtitle>
                      <v-card-text class="pa-0">
                        <v-data-table
                          :headers="stock_history_headers"
                          :items="[
                            {
                              date_range: formatDateRange(warehouse.start_date, warehouse.end_date),
                              total_added: warehouse.total_added,
                              total_reduced: warehouse.total_reduced,
                              current_stock: warehouse.current_stock,
                              period_end_stock: warehouse.period_end_stock,
                            },
                          ]"
                          hide-default-footer
                          class="elevation-0"
                          dense
                        >
                          <template v-slot:item.total_added="{ item }">
                            <span style="color: #388e3c; font-weight: 500;">+{{ formatNumber(item.total_added) }}</span>
                          </template>
                          <template v-slot:item.total_reduced="{ item }">
                            <span style="color: #d32f2f; font-weight: 500;">-{{ formatNumber(item.total_reduced) }}</span>
                          </template>
                          <template v-slot:item.current_stock="{ item }">
                            <span class="font-weight-medium">{{ formatNumber(item.current_stock) }}</span>
                          </template>
                          <template v-slot:item.period_end_stock="{ item }">
                            <span class="font-weight-medium">{{ formatNumber(item.period_end_stock) }}</span>
                          </template>
                        </v-data-table>
                      </v-card-text>
                    </v-card>
                  </div>
                </div>
                <div v-else class="pa-4 text-center grey--text">
                  ไม่พบข้อมูลสต๊อกสำหรับสินค้านี้ในช่วงวันที่ที่เลือก
                </div>
              </v-card-text>
            </v-card>
          </div>
        </div>

        <v-row v-else-if="!loading_stock_data && stock_data_results.length === 0 && has_searched">
          <v-col cols="12" class="text-center">
            <div class="grey--text">ไม่พบข้อมูล</div>
          </v-col>
        </v-row>
        </template>

        <!-- โหมด Supplier -->
        <template v-if="stock_mode === 'supplier'">
          <v-row class="mb-2">
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="selected_suppliers_stock"
                :items="suppliers"
                :item-text="displaySupplier"
                item-value="id"
                label="เลือกผู้จำหน่าย *"
                color="#86bc3a"
                multiple
                chips
                deletable-chips
                prepend-inner-icon="mdi-truck-delivery"
                outlined
                dense
                small-chips
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="selected_products_supplier"
                :items="products"
                item-text="name"
                item-value="id"
                label="กรองตามสินค้า (ไม่เลือก = ทั้งหมด)"
                color="#86bc3a"
                multiple
                chips
                deletable-chips
                outlined
                dense
                small-chips
                hide-details="auto"
                clearable
              />
            </v-col>
          </v-row>
          <v-row class="mb-2">
            <v-col cols="12" md="4">
              <v-menu
                v-model="start_date_menu_supplier"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="start_date_stock"
                    label="วันที่เริ่มต้น *"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    color="#86bc3a"
                    outlined
                    dense
                    hide-details="auto"
                  />
                </template>
                <v-date-picker
                  v-model="start_date_stock"
                  @input="start_date_menu_supplier = false"
                  color="#86bc3a"
                />
              </v-menu>
            </v-col>
            <v-col cols="12" md="4">
              <v-menu
                v-model="end_date_menu_supplier"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="end_date_stock"
                    label="วันที่สิ้นสุด *"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    color="#86bc3a"
                    outlined
                    dense
                    hide-details="auto"
                    :min="start_date_stock"
                  />
                </template>
                <v-date-picker
                  v-model="end_date_stock"
                  @input="end_date_menu_supplier = false"
                  color="#86bc3a"
                  :min="start_date_stock"
                />
              </v-menu>
            </v-col>
          </v-row>
          <v-row v-if="loading_supplier_purchases" class="mb-4">
            <v-col cols="12" class="text-center">
              <v-progress-circular indeterminate color="#86bc3a" />
              <div class="mt-2">กำลังโหลดข้อมูล...</div>
            </v-col>
          </v-row>
          <div v-else-if="supplier_purchase_results.length > 0" class="mt-4">
            <v-data-table
              :headers="supplier_headers"
              :items="supplier_purchase_results"
              :items-per-page="-1"
              class="elevation-1"
              dense
              hide-default-footer
            >
              <template v-slot:item.supplier_name="{ item }">
                <span class="font-weight-medium">
                  {{ item.supplier_name }}
                  <span v-if="item.supplier_department"> ({{ item.supplier_department }})</span>
                </span>
                <span v-if="item.supplier_code" class="grey--text caption ml-1">
                  ({{ item.supplier_code }})
                </span>
              </template>
              <template v-slot:item.total_qty="{ item }">
                <span class="font-weight-medium">{{ formatNumber(item.total_qty) }}</span>
              </template>
              <template v-slot:item.total_amount="{ item }">
                <span class="font-weight-medium">{{ formatNumber(item.total_amount) }}</span>
              </template>
            </v-data-table>
          </div>
          <v-row v-else-if="!loading_supplier_purchases && supplier_purchase_results.length === 0 && has_searched_supplier">
            <v-col cols="12" class="text-center">
              <div class="grey--text">ไม่พบข้อมูลซื้อตามผู้จำหน่ายในช่วงวันที่ที่เลือก</div>
            </v-col>
          </v-row>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          v-if="stock_mode === 'stock'"
          color="#86bc3a"
          class="white--text"
          @click="fetchProductStock"
          :disabled="!canSearchStock"
          :loading="loading_stock_data"
        >
          <v-icon left>mdi-magnify</v-icon>
          ค้นหา
        </v-btn>
        <v-btn
          v-if="stock_mode === 'supplier'"
          color="#86bc3a"
          class="white--text"
          @click="fetchSupplierPurchases"
          :disabled="!canSearchSupplier"
          :loading="loading_supplier_purchases"
        >
          <v-icon left>mdi-magnify</v-icon>
          ค้นหา
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import { HTTP } from '@/axios';

export default {
  name: 'PurchaseStockPage',
  data() {
    return {
      stock_mode: 'stock',
      products: [],
      selected_products_stock: [],
      selected_branch_stock: null,
      branches_stock: [],
      start_date_stock: null,
      end_date_stock: null,
      start_date_menu: false,
      end_date_menu: false,
      loading_stock_data: false,
      stock_data_results: [],
      has_searched: false,
      stock_history_headers: [
        { text: 'ช่วงวันที่', value: 'date_range', sortable: false },
        { text: 'จำนวนเพิ่มรวม', value: 'total_added', sortable: false, align: 'right' },
        { text: 'จำนวนลดรวม', value: 'total_reduced', sortable: false, align: 'right' },
        { text: 'stock ปัจจุบัน', value: 'current_stock', sortable: false, align: 'right' },
        { text: 'stock ณ สิ้นสุดช่วง', value: 'period_end_stock', sortable: false, align: 'right' },
      ],
      suppliers: [],
      selected_suppliers_stock: [],
      selected_products_supplier: [],
      start_date_menu_supplier: false,
      end_date_menu_supplier: false,
      loading_supplier_purchases: false,
      supplier_purchase_results: [],
      has_searched_supplier: false,
      supplier_headers: [
        { text: 'ผู้จำหน่าย', value: 'supplier_name', sortable: false },
        { text: 'รหัสสินค้า', value: 'product_code', sortable: false },
        { text: 'ชื่อสินค้า', value: 'product_name', sortable: false },
        { text: 'จำนวนรวม', value: 'total_qty', sortable: false, align: 'right' },
        { text: 'ยอดรวม (บาท)', value: 'total_amount', sortable: false, align: 'right' },
      ],
    };
  },
  computed: {
    canSearchStock() {
      return (
        this.selected_products_stock.length > 0 &&
        this.start_date_stock &&
        this.end_date_stock &&
        new Date(this.start_date_stock) <= new Date(this.end_date_stock)
      );
    },
    canSearchSupplier() {
      return (
        this.selected_suppliers_stock.length > 0 &&
        this.start_date_stock &&
        this.end_date_stock &&
        new Date(this.start_date_stock) <= new Date(this.end_date_stock)
      );
    },
  },
  methods: {
    displaySupplier(item) {
      if (!item) return '';
      const name = item.name || '';
      // รองรับทั้ง supplier_department (จาก API supplier-purchases) และ department (จาก /suppliers)
      const dept = item.supplier_department || item.department || '';
      if (name && dept) return `${name} (${dept})`;
      return name || dept || '';
    },
    formatNumber(val) {
      const n = parseFloat(val || 0);
      return n.toLocaleString('th-TH', { maximumFractionDigits: 2 });
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const d = new Date(dateString);
      if (Number.isNaN(d.getTime())) return dateString;
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    },
    formatDateRange(start, end) {
      if (!start || !end) return '-';
      return `${this.formatDate(start)} - ${this.formatDate(end)}`;
    },
    getProductName(productId) {
      const p = this.products.find(p => p.id === productId);
      return p ? p.name : `รหัสสินค้า ${productId}`;
    },
    async fetchBranchesStock() {
      try {
        const response = await HTTP.get('/sales-history/branches');
        if (response.data.success) {
          const branches = response.data.data || [];
          // เพิ่ม option สำหรับคลังหลักให้เลือกได้จาก dropdown สาขา
          this.branches_stock = [
            { id: 'MAIN', name: 'คลังหลักฟามาแคร์' },
            ...branches,
          ];
        }
      } catch (error) {
        console.error('Error fetching branches:', error);
      }
    },
    async fetchProductStock() {
      if (!this.canSearchStock) return;

      this.loading_stock_data = true;
      this.has_searched = true;
      this.stock_data_results = [];

      try {
        const products = this.selected_products_stock
          .map(productId => {
            const product = this.products.find(p => p.id === productId);
            return product
              ? {
                  product_id: productId,
                  product_code: product.product_code,
                }
              : null;
          })
          .filter(p => p !== null);

        if (products.length === 0) {
          this.loading_stock_data = false;
          return;
        }

        const requestData = {
          products,
          start_date: this.start_date_stock,
          end_date: this.end_date_stock,
        };
        if (this.selected_branch_stock && this.selected_branch_stock !== 'MAIN') {
          requestData.branch_id = this.selected_branch_stock;
        }

        const response = await HTTP.post('/purchaseorders/getproductsstockhistory', requestData);

        if (response.data.success) {
          const responseData = response.data.object || [];

          this.stock_data_results = products
            .map(product => {
              const productId = product.product_id;
              const productInfo = this.products.find(p => p.id === productId);

              let item = responseData.find(
                r => r.product_id === productId || r.product_code === product.product_code,
              );

              if (!item) {
                return null;
              }

              let warehouseData = item.warehouses || [];

              // กรณีเลือกสาขา / คลังหลัก ให้กรองเฉพาะสาขาที่เลือก
              if (this.selected_branch_stock && Array.isArray(warehouseData)) {
                if (this.selected_branch_stock === 'MAIN') {
                  warehouseData = warehouseData.filter(w => w.is_main);
                } else {
                  const selectedBranch = this.branches_stock.find(b => b.id === this.selected_branch_stock);
                  if (selectedBranch) {
                    warehouseData = warehouseData.filter(
                      w =>
                        w.warehouse === selectedBranch.name ||
                        w.warehouse === selectedBranch.branch_name ||
                        (w.warehouse && w.warehouse.includes(selectedBranch.name)),
                    );
                  }
                }

                // ถ้าไม่มีข้อมูล warehouse สำหรับสาขาที่เลือกเลย
                // ให้สร้างแถวเปล่าของสาขานั้น พร้อมค่า 0 ทั้งหมด เพื่อให้เห็นว่ามีสาขานี้แต่ไม่มีสต๊อก
                if (warehouseData.length === 0) {
                  if (this.selected_branch_stock === 'MAIN') {
                    warehouseData = [
                      {
                        warehouse: 'คลังหลักฟามาแคร์',
                        is_main: true,
                        current_stock: 0,
                        period_end_stock: 0,
                        total_added: 0,
                        total_reduced: 0,
                        start_date: this.start_date_stock,
                        end_date: this.end_date_stock,
                        data_other: [],
                      },
                    ];
                  } else {
                    const selectedBranch = this.branches_stock.find(b => b.id === this.selected_branch_stock);
                    if (selectedBranch) {
                      warehouseData = [
                        {
                          warehouse: selectedBranch.name,
                          is_main: false,
                          current_stock: 0,
                          period_end_stock: 0,
                          total_added: 0,
                          total_reduced: 0,
                          start_date: this.start_date_stock,
                          end_date: this.end_date_stock,
                          data_other: [],
                        },
                      ];
                    }
                  }
                }
              }

              return {
                product_id: productId,
                product_code: product.product_code,
                product_name: productInfo?.name || '',
                data: Array.isArray(warehouseData) ? warehouseData : [],
              };
            })
            .filter(r => r !== null && r !== undefined);
        }
      } catch (error) {
        console.error('Error fetching product stock history:', error);
      } finally {
        this.loading_stock_data = false;
      }
    },
    async fetchSuppliers() {
      try {
        const response = await HTTP.get('/purchaseorders/suppliers');
        const raw = response.data.object ?? response.data.data ?? response.data;
        this.suppliers = Array.isArray(raw) ? raw : [];
      } catch (error) {
        console.error('Error fetching suppliers:', error);
      }
    },
    async fetchSupplierPurchases() {
      if (!this.canSearchSupplier) return;

      this.loading_supplier_purchases = true;
      this.has_searched_supplier = true;
      this.supplier_purchase_results = [];

      try {
        const payload = {
          start_date: this.start_date_stock,
          end_date: this.end_date_stock,
          supplier_ids: this.selected_suppliers_stock,
        };
        if (this.selected_products_supplier && this.selected_products_supplier.length > 0) {
          payload.product_ids = this.selected_products_supplier;
        }

        const response = await HTTP.post('/purchaseorders/supplier-purchases', payload);

        const raw = response.data.object ?? response.data.data ?? response.data;
        const list = Array.isArray(raw) ? raw : [];
        // จัดกลุ่มให้ Supplier เดียวกัน + department เดียวกัน อยู่ติดกัน
        this.supplier_purchase_results = list.sort((a, b) => {
          const nameA = (a.supplier_name || '').toString().toLowerCase();
          const nameB = (b.supplier_name || '').toString().toLowerCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;

          const deptA = (a.supplier_department || '').toString().toLowerCase();
          const deptB = (b.supplier_department || '').toString().toLowerCase();
          if (deptA < deptB) return -1;
          if (deptA > deptB) return 1;

          const codeA = (a.supplier_code || '').toString().toLowerCase();
          const codeB = (b.supplier_code || '').toString().toLowerCase();
          if (codeA < codeB) return -1;
          if (codeA > codeB) return 1;

          return 0;
        });
      } catch (error) {
        console.error('Error fetching supplier purchases:', error);
      } finally {
        this.loading_supplier_purchases = false;
      }
    },
  },
  async created() {
    // โหลดสินค้า
    try {
      const res = await HTTP.post('/purchaseorders/products');
      if (res.data.success) {
        this.products = res.data.products;
      }
    } catch (e) {
      console.error(e);
    }

    // default ช่วงวันที่ย้อนหลัง 30 วัน
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 29);
    const toYMD = d => d.toISOString().slice(0, 10);
    this.start_date_stock = toYMD(start);
    this.end_date_stock = toYMD(end);

    // โหลดสาขา และรายชื่อผู้จำหน่าย (สำหรับโหมด Supplier)
    this.fetchBranchesStock();
    this.fetchSuppliers();
  },
};
</script>

